import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { serverErrorMessage } from "@core/helpers";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { UntypedFormBuilder } from "@angular/forms";
import { CoreConfigService } from "@core/services/config.service";
import { PaymentService } from "@core/services/payment.service";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { AuthenticationService } from "@core/services/auth";
import { Location } from "@angular/common";
import { User } from "@core/models";
import { CareersService } from "app/main/careers/services/careers.service";
import {
  CareersPlan,
  CareersSubscription,
} from "app/main/careers/models/career";
import { SubscriptionPlan } from "@core/models/subscription";

export enum paymentStages {
  PROVIDE_PAYMENT_PLAN = 1,
  DISPLAY_PAYMENT_INSTRUCTIONS = 2,
  CONRIFM_PAYMENT = 3,
  DISPLAY_PAYMENT_VERIFY = 4,
  DISPLAY_PAYMENT_SUCCESS = 5,
  DISPLAY_PAYMENT_FAILED = 6,
  DISPLAY_PAYMENT_SUCCESS_FOR_MOBILE = 7,
}

@Component({
  standalone: false,
  selector: "app-upgrade-page",
  templateUrl: "./upgrade-page.component.html",
  styleUrls: ["./upgrade-page.component.scss"],
})
export class UpgradePageComponent implements OnInit, OnDestroy {
  public coreConfig: any;

  // Private
  private _unsubscribeAll: Subject<any>;
  @Input() plans: Array<SubscriptionPlan> = [];
  @Input() isLoadingPlans = false;
  @Input() planName: string;

  @Output() processStageEvt: EventEmitter<any> = new EventEmitter<any>();
  @Output() cancelPaymentProcessEvt: EventEmitter<any> =
    new EventEmitter<any>();
  public paymentStage = paymentStages;
  isLoading = false;
  tokenId!: any;
  public upgradeStatus = "pending";
  private inter: any;
  private counter: number = 0;
  // M-Pesa STK confirmations routinely take longer than the ~35s this used
  // to allow (10s initial wait + 5 polls * 5s); polling now runs for ~2min
  // before giving up, to avoid a false "payment failed" while it's still
  // pending confirmation on the user's phone.
  private readonly MAX_SUBSCRIPTION_POLL_ATTEMPTS = 24;
  public subscription: any;
  public data: any;
  private hasHistory: boolean;
  careersPlan: CareersPlan;
  private destroy$ = new Subject();
  private _helpers = inject(HelperFunctionsService);
  private _destroyRef = inject(DestroyRef);
  unlockedCourseIds: number[];
  careerTopicId: number;
  subscriptions: CareersSubscription[];
  originalPlans: SubscriptionPlan[];

  paymentInfo: any = {};
  processState = paymentStages.PROVIDE_PAYMENT_PLAN;
  isPageView = false;
  step: number = 1;
  totalSteps = 2;
  progress = 0;
  active = 1;
  beewizSubscription: boolean;
  /** Set when a parent is subscribing a dependant (?dependantId=) rather than themselves */
  dependantId: number | null = null;

  constructor(
    private _coreConfigService: CoreConfigService,
    private paymentService: PaymentService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private _authenticationService: AuthenticationService,
    private formBuilder: UntypedFormBuilder,
    private _location: Location,
    private careersService: CareersService
  ) {
    this._unsubscribeAll = new Subject();
    this.hasHistory = this.router.navigated;
  }

  ngOnInit(): void {
    this.tokenId = this.route.snapshot.queryParams.token;
    this.careerTopicId = this.route.snapshot.queryParams.course;
    const dependantIdParam = this.route.snapshot.queryParams.dependantId;
    this.dependantId = dependantIdParam != null ? Number(dependantIdParam) : null;

    // if mobile beewiz subscription, set beewiz as active tab
    this.beewizSubscription = this.route.snapshot.queryParams.sT === "beewiz";
    if (this.beewizSubscription) this.active = 2;

    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });

    if (this.tokenId != null || this.tokenId != undefined) {
      this.initMobile();
    } else {
      this.getPlans();
      this.initWebApp();
    }
  }

  initWebApp() {
    this.isPageView = false;
  }

  initMobile() {
    this.isPageView = true;
    this.configureLayout();
    this.getTokenId();
  }

  configureLayout() {
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.unsubscribe();
    clearInterval(this.inter);
  }

  getTokenId(): void {
    if (!this.tokenId) {
      return;
    }

    if (this.careerTopicId) {
      this.planName = "careers";
      // emit something
    }

    this.paymentService.getTokenId(this.tokenId).subscribe(
      (respData) => {
        let tok: any = {};
        tok.access_token = respData.accessToken;
        this._authenticationService.setTokens(tok).subscribe(() => {
          this.getPlans();
        });
      },
      (error) => {
        console.error(error);
      }
    );
  }

  getPlans() {
    const hasDiscount = this.route.snapshot.queryParams.xwlk === "p3j4h";
    const failedPaymentDiscount = this.route.snapshot.queryParams.s;

    if (failedPaymentDiscount) {
      this.getNormalPlans(failedPaymentDiscount);
      return;
    }

    if (hasDiscount) {
      this.getDiscountedPlans();
      return;
    }

    if (this.planName === "careers") {
      this.getCareerPlans();
      return;
    }

    this.getNormalPlans();
  }

  getFailedPaymentDiscountedPlans(): void {
    this.isLoadingPlans = true;
  }

  getDiscountedPlans(): void {
    this.isLoadingPlans = true;
    this.paymentService
      .getDiscountedPlans(this.dependantId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res: SubscriptionPlan[]) => {
          this.isLoadingPlans = false;
          this.plans = res;
          this.originalPlans = this.plans;
        },
        error: (err: HttpErrorResponse) => this.plansFailed(err),
      });
  }

  private plansFailed(err: HttpErrorResponse): void {
    this.isLoadingPlans = false;
    this._helpers.showWarningToastr(serverErrorMessage(err, "Couldn't load the plans. Please try again."));
  }

  getNormalPlans(discountCode?: string): void {
    this.isLoadingPlans = true;
    this.paymentService
      .getPlans(discountCode, this.dependantId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (data: SubscriptionPlan[]) => {
          this.plans = data;
          this.originalPlans = this.plans;
          // pass normal string
          if (this.beewizSubscription) {
            this.filterPlans("BEEWIZ");
          } else {
            this.filterPlans("NORMAL");
          }
          this.isLoadingPlans = false;
        },
        error: (err: HttpErrorResponse) => this.plansFailed(err),
      });
  }

  // second approach
  getCareerPlans(): void {
    this.isLoadingPlans = true;
    this.careersService
      .getCoursesAndAmount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.isLoadingPlans = false;
          this.careersPlan = res;
        },
        (err) => {
          this.isLoadingPlans = false;
          console.error(err);
        }
      );
  }

  updateProcessState(state: number) {
    this.processState = state;
    this.processStageEvt.emit(state);
    // update stepper
    this.updateStep(state);
  }

  // stepper
  updateStep(state: number): void {
    state > 1 ? (this.step = 2) : (this.step = 1);
    this.updateProgress();
  }

  updateProgress() {
    this.progress = ((this.step - 1) / (this.totalSteps - 1)) * 100;
  }

  //get plan from plan component
  getPaymentPlan(selectedPlan: any) {
    this.paymentInfo.plan = this.plans[selectedPlan.planIndex];
    this.paymentInfo.phone = selectedPlan.phone;

    if (selectedPlan.discountCode)
      this.paymentInfo.discountCode = selectedPlan.discountCode;

    if (this.careerTopicId != undefined || this.careerTopicId != null) {
      this.paymentInfo.careerTopic = this.careersPlan?.courses.find(
        (topic) => topic.topicId == this.careerTopicId
      );
    } else {
      this.paymentInfo.careerTopic = this.careersPlan?.courses.find(
        (topic) => topic.topicId == selectedPlan.planIndex
      );
    }
    this.updateProcessState(paymentStages.CONRIFM_PAYMENT);
  }

  cancelPaymentInfo() {
    this.updateProcessState(paymentStages.PROVIDE_PAYMENT_PLAN);
  }

  approvePaymentInfo() {
    this.addSubscription();
  }

  addSubscription() {
    this.counter = 0;
    this.updateProcessState(paymentStages.DISPLAY_PAYMENT_INSTRUCTIONS);
    if (!this.isLoading) {
      this.isLoading = true;

      this.getPaymentObservable().subscribe(
        () => {
          this.isLoading = false;
          this.upgradeStatus = "sent";
          // this.showSuccess(res.message);

          setTimeout(() => {
            this.checkSubscription();
          }, 10000);
        },
        (err) => {
          this.updateProcessState(paymentStages.DISPLAY_PAYMENT_FAILED);
          this.isLoading = false;
          this._helpers.showWarningToastr(
            serverErrorMessage(err, "Couldn't send the payment request. Please try again.")
          );
        }
      );
    }
  }

  getPaymentObservable() {
    if (this.planName === "careers") {
      const careerPaymentInfo = {
        amount: this.careersPlan.amount,
        phoneNumber: this.paymentInfo.phone,
        courseId: this.paymentInfo.careerTopic.topicId,
      };
      return this.careersService.requestCareerVideoStkPush(careerPaymentInfo);
    }
    const data = this.processFormData();
    return this.paymentService.addSubscription(data);
  }

  processFormData() {
    this.data = this.paymentInfo;
    let formData: any = {};
    if (this.data.plan) {
      formData.amount = this.data.plan.hasDiscount
        ? this.data.plan.discountedAmount
        : this.data.plan.amount;
      formData.planId = this.data.plan.planId;
      formData.phoneNumber = this.data.phone;
      if (this.data.discountCode)
        formData.discountCode = this.data.discountCode;
      if (this.dependantId != null) formData.studentId = this.dependantId;
    }
    return formData;
  }

  checkSubscription() {
    this.upgradeStatus = "awaiting";

    if (this.planName === "careers") {
      this.updateProcessState(paymentStages.DISPLAY_PAYMENT_VERIFY);
      setTimeout(() => {
        this.getCareerSubscription();
      }, 2000);
      return;
    }

    setTimeout(() => {
      this.updateProcessState(paymentStages.DISPLAY_PAYMENT_VERIFY);
    }, 3000);

    if (this.dependantId != null) {
      // GET /subscription reflects the parent's own account, not the
      // dependant's, so it can never confirm here. Show success optimistically
      // once the STK push has been sent, same as the CBC parent flow does,
      // then return to the subscriptions list.
      setTimeout(() => {
        this.updateProcessState(paymentStages.DISPLAY_PAYMENT_SUCCESS);
        setTimeout(() => this.router.navigate(['/cbc/dashboard/subscribe']), 3000);
      }, 6000);
      return;
    }

    this.inter = setInterval(() => {
      this.getSubscription();
    }, 5000);
  }

  getSubscription() {
    if (this.counter >= this.MAX_SUBSCRIPTION_POLL_ATTEMPTS) {
      clearInterval(this.inter);
      // this.clearInterval(this.inter);
      this.updateProcessState(paymentStages.DISPLAY_PAYMENT_FAILED);
      return;
    }
    this.counter++;
    this.paymentService.getSubscription().subscribe(
      (data) => {
        console.log("Subscription status:", data);
        this.subscription = data.subscription;
        if (this.subscription && (this.subscription.subscribed || this.subscription.beewizSubscription)) {
          clearInterval(this.inter);
          this.applySubscriptionSuccess();
        }
      },
      () => {
        this.updateProcessState(paymentStages.DISPLAY_PAYMENT_FAILED);
      }
    );
  }

  private applySubscriptionSuccess(): void {
    let paymentState = paymentStages.DISPLAY_PAYMENT_SUCCESS_FOR_MOBILE;
    if (!this.isPageView) {
      let currentUser = JSON.parse(localStorage.getItem("currentUser"));
      currentUser.subscription = this.subscription;
      localStorage.setItem("currentUser", JSON.stringify(currentUser));
      paymentState = paymentStages.DISPLAY_PAYMENT_SUCCESS;
    }
    this.updateProcessState(paymentState);
    setTimeout(() => {
      this.loadHomepage();
    }, 3000);
  }

  // Bound to the "Retry" button on the failure screen. The polling window
  // giving up doesn't mean the payment failed - it may have just confirmed
  // late. Check status once before firing a brand new STK push, so a user
  // whose payment already went through isn't prompted to pay again.
  retryAfterFailure(): void {
    this.paymentService.getSubscription().subscribe({
      next: (data) => {
        this.subscription = data.subscription;
        const alreadySubscribed =
          this.subscription &&
          (this.subscription.subscribed || this.subscription.beewizSubscription);
        if (alreadySubscribed) {
          this.applySubscriptionSuccess();
          return;
        }
        this.addSubscription();
      },
      error: () => this.addSubscription(),
    });
  }

  getCareerSubscription(): void {
    let intervalId;
    let counter = 0;

    intervalId = setInterval(() => {
      if (counter >= 3) {
        clearInterval(intervalId);
        this.updateProcessState(paymentStages.DISPLAY_PAYMENT_FAILED);
        return;
      }

      counter++;
      this.careersService.getUnlockedCareerTopics().subscribe(
        (res) => {
          if (res.length) {
            this.subscriptions = res;
            this.unlockedCourseIds = res.map((res) => res.courseId);
            if (
              this.checkIfCourseIsBought(this.paymentInfo.careerTopic.topicId)
            ) {
              clearInterval(intervalId);
              let paymentState =
                paymentStages.DISPLAY_PAYMENT_SUCCESS_FOR_MOBILE;
              if (!this.isPageView) {
                paymentState = paymentStages.DISPLAY_PAYMENT_SUCCESS;
              }
              this.updateProcessState(paymentState);
              setTimeout(() => {
                this.loadHomepage();
                this.router.navigate(["/careers/home"]);
              }, 3000);
            }
          }
        },
        () => {
          this.updateProcessState(paymentStages.DISPLAY_PAYMENT_FAILED);
        }
      );
    }, 10000);
  }

  checkIfCourseIsBought(courseId: number): boolean {
    if (this.unlockedCourseIds.length) {
      return this.unlockedCourseIds.includes(courseId);
    }
  }

  getExpiryDate() {
    let expiryDate;
    if (this.planName === "careers") {
      if (this.checkIfCourseIsBought(this.paymentInfo.careerTopic.topicId)) {
        this.subscription = this.subscriptions.find(
          (sub) => sub.courseId === this.paymentInfo.careerTopic.topicId
        );
        expiryDate = new Date(this.subscription.expiresAt);
        expiryDate = expiryDate.toLocaleString("en-GB", { timeZone: "EAT" });
      }
    } else if (this.subscription) {
      // Dependant payments skip the GET /subscription poll (it reflects the
      // parent's own account), so this.subscription is never set there.
      expiryDate = new Date(this.subscription.expiresAt);
      expiryDate = expiryDate.toLocaleString("en-GB", { timeZone: "EAT" });
    }
    return expiryDate;
  }

  getPlanName() {
    let planname;
    if (this.planName === "careers") {
      planname = this.paymentInfo.careerTopic.title;
    } else {
      planname = this.data && this.data.plan ? this.data.plan.planName : "-";
    }
    return planname;
  }

  loadHomepage() {
    // initiate close payment popup,or
    !this.isPageView
      ? this.cancelPaymentProcessEvt.emit()
      : this._authenticationService.logout();
  }

  // clearInterval(timer) {
  //     if (timer) clearInterval(timer);
  // }

  // showSuccess(msg) {
  //     this.toastr.success(`🙌 ${msg}`, 'Success!', {
  //         timeOut: 5000,
  //         toastClass: 'toast ngx-toastr',
  //         closeButton: true
  //     });
  // }

  // showFailed(msg) {
  //     this.toastr.warning(`⚠️ ${msg}`, 'Failed!', {
  //         timeOut: 5000,
  //         toastClass: 'toast ngx-toastr',
  //         closeButton: true
  //     });
  // }

  padTo2Digits(num) {
    return num.toString().padStart(2, "0");
  }

  formatDate(date) {
    return (
      [
        this.padTo2Digits(date.getMonth() + 1),
        this.padTo2Digits(date.getDate()),
        date.getFullYear(),
      ].join("/") +
      " " +
      [
        this.padTo2Digits(date.getHours()),
        this.padTo2Digits(date.getMinutes()),
        // this.padTo2Digits(date.getSeconds()),
      ].join(":")
    );
  }

  // discount
  getDiscountCode(discountCode: string): void {
    this.getNormalPlans(discountCode);
  }

  onTabChange(): void {
    if (this.active == 1) this.filterPlans("BEEWIZ");
    if (this.active == 2) this.filterPlans("NORMAL");
  }

  filterPlans(planType: string): void {
    this.plans = this.originalPlans.filter((plan) => plan.planType == planType);
  }
}
